/* eslint-disable */

import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import useHttp from "../../hooks/use-http";
import { useTitle } from "../../hooks/useTitle";
import Spinner from "../Spinner";
import PayAttitude from "../../assets/images/payattitude.png";
import CardContext from "../../store/card-context";
import toast from "react-hot-toast";

const PayWithPhone = () => {
  const navigate = useNavigate();
  const outletContext = useOutletContext() || {};
  const { transactionInfo } = outletContext;
  const [timer, setTimer] = useState("03:00");
  const [disableButton, setDisableButton] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);
  const { sendRequest: postTransaction, isLoading } = useHttp();
  const { isDeclined, setIsDeclined } = useContext(CardContext);

  // Sandbox simulation states
  const [simulationType, setSimulationType] = useState("approved");

  const { id } = useParams();
  useTitle("Phone");

  const isTestMode =
    transactionInfo?.data &&
    (transactionInfo.data.isLive === false || transactionInfo.data.isLive === "false");

  useEffect(() => {
    const transactDeclined = JSON.parse(localStorage.getItem("isDeclined"));
    if (isDeclined || transactDeclined) {
      toast.error("Please try another payment method");
    }
    setIsDeclined(false);
    localStorage.setItem("isDeclined", JSON.stringify(false));
  }, [isDeclined, setIsDeclined]);

  useEffect(() => {
    if (simulationType === "approved") {
      setPhoneNumber("08069493993");
    } else {
      setPhoneNumber("00000000000");
    }
  }, [simulationType]);

  const startTimer = () => {
    const [minutes, seconds] = timer.split(":").map(Number);
    let totalSeconds = minutes * 60 + seconds;

    if (totalSeconds <= 0) {
      setDisableButton(true);
      return;
    }

    const timerInterval = setInterval(() => {
      totalSeconds--;

      if (totalSeconds <= 0) {
        setTimer("00:00");
        setDisableButton(true);
        clearInterval(timerInterval);
      } else {
        const newMinutes = Math.floor(totalSeconds / 60);
        const newSeconds = totalSeconds % 60;

        setTimer(
          `${newMinutes.toString().padStart(2, "0")}:${newSeconds
            .toString()
            .padStart(2, "0")}`
        );
      }
    }, 1000);

    return () => clearInterval(timerInterval);
  };

  const confirmTransaction = (data) => {
    setDisableButton(false);
    navigate(`/${id}/payment-confirmation/${id}/sandbox-pay`);
  };

  const authorisePayment = (event) => {
    if (event) event.preventDefault();
    if (phoneNumber === "" || phoneNumber.length < 10 || phoneNumber === null) {
      toast.error("Please enter a valid phone number");
      return;
    }

    let transaction = {
      method: "payattitude",
      mobile: phoneNumber,
    };
    setDisableButton(true);
    startTimer();
    postTransaction(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/sandbox/checkout/${id}/pay`,
        method: "POST",
        body: transaction,
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      },
      confirmTransaction
    );
  };

  const handlePhoneNumberChange = (event) => {
    const inputText = event.target.value.replace(/\D/g, "");
    const truncatedInput = inputText.slice(0, 11);
    setPhoneNumber(truncatedInput);
  };

  const currentCurrencySymbol = transactionInfo?.data?.currency === "NGN" ? "₦" : (transactionInfo?.data?.currency || "NGN");
  const totalAmountValue = (transactionInfo?.data?.amount || 0) + (transactionInfo?.data?.fee || 0);

  return (
    <div className="w-full max-w-[560px] mx-auto py-2 px-4 sm:px-0">
      {/* Test Transaction Ribbon */}
      {isTestMode && (
        <div
          id="test-transaction-ribbon"
          data-testid="test-transaction-ribbon"
          className="w-full mb-5"
        >
          <div className="w-full bg-gradient-to-r from-red-600 via-rose-600 to-red-600 text-white text-xs sm:text-sm font-bold py-2.5 px-4 rounded-lg shadow-sm border border-red-700/30 flex items-center justify-center gap-2 uppercase tracking-wider text-center">
            <svg
              className="w-4 h-4 flex-shrink-0 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span>TEST TRANSACTION</span>
          </div>
        </div>
      )}

      <div className="text-center px-4">
        <p className="text-slate-600 text-sm leading-relaxed mb-6">
          To pay <span className="font-bold text-slate-800">{currentCurrencySymbol}{totalAmountValue.toLocaleString()}</span>, enter the phone number linked to your account.
        </p>
      </div>

      <form onSubmit={authorisePayment} className="space-y-6">
        <div className="text-center">
          <p className="text-[11px] text-slate-400 leading-relaxed font-normal max-w-[440px] mx-auto">
            Enter your phone number, then follow the USSD prompt or app notification to complete payment before the timer expires.
          </p>
        </div>

        {/* Sandbox Simulation Options */}
        <div className="bg-slate-50 p-4 border border-slate-200 rounded-lg">
          <span className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-wider mb-3 text-center">
            Select a simulation flow
          </span>
          <div className="flex flex-col gap-2">
            {[
              { id: "approved", label: "Simulate Success (Approved)", desc: "Push notification/USSD approved outcome", badge: "Approved", color: "bg-emerald-50 text-emerald-700 border-emerald-100", phone: "08069493993" },
              { id: "declined", label: "Simulate Decline (Declined)", desc: "Push notification/USSD declined outcome", badge: "Declined", color: "bg-rose-50 text-rose-700 border-rose-100", phone: "00000000000" },
            ].map((opt) => (
              <div
                key={opt.id}
                onClick={() => setSimulationType(opt.id)}
                className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${simulationType === opt.id
                  ? "border-[#00aeef] bg-sky-50/40 shadow-sm"
                  : "border-slate-100 hover:border-slate-200 bg-white"
                  }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${simulationType === opt.id ? "border-[#00aeef]" : "border-slate-300"
                    }`}>
                    {simulationType === opt.id && <div className="w-2 h-2 rounded-full bg-[#00aeef]" />}
                  </div>
                  <div className="flex flex-col text-left">
                    <span className="text-xs font-bold text-slate-800">{opt.label}</span>
                    <span className="text-[10px] text-slate-400 font-mono mt-0.5">{opt.phone}</span>
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold border ${opt.color}`}>
                  {opt.badge}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-center justify-center gap-1.5 pt-1">
          <div className="flex items-center gap-1">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Expires:</span>
            <span className="text-xs font-bold text-red-500 font-mono bg-red-50 px-2 py-0.5 rounded border border-red-100">{timer}</span>
          </div>
        </div>

        <button
          type="submit"
          className="w-full h-[54px] bg-[#00aeef] hover:bg-[#0096ce] active:scale-[0.99] text-white rounded-lg font-bold text-base cursor-pointer transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          disabled={disableButton || isLoading}
        >
          {isLoading ? (
            <Spinner />
          ) : (
            `Pay ${currentCurrencySymbol}${totalAmountValue.toLocaleString()}`
          )}
        </button>

        <div className="relative flex justify-center pt-2">
          <div
            className="cursor-pointer text-xs font-bold text-[#00aeef] hover:underline"
            onClick={() => setShowTooltip(!showTooltip)}
          >
            How to Pay with Phone Numbers
          </div>
          {showTooltip && (
            <div className="absolute bottom-full mb-3 w-[290px] bg-slate-800 text-white text-[11px] font-normal rounded-lg p-4 shadow-xl z-50 text-left leading-relaxed">
              <p className="mb-2">
                After clicking 'Pay', you will receive a push notification on your Payattitude app or a USSD prompt on your phone to authorize this payment. Ensure your phone number is linked to your account in any of the banks below:
              </p>
              <ul className="list-disc pl-4 space-y-0.5 text-slate-300">
                <li>Paytent</li>
                <li>Hope PS Bank</li>
                <li>UBA</li>
                <li>Access Bank</li>
                <li>Fidelity</li>
                <li>Ecobank</li>
                <li>Zenith Bank</li>
              </ul>
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-slate-800"></div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default PayWithPhone;